/* ==========================================================================
   DatouBoss Solar Calculator - script
   All globals are prefixed with `sc` / `scState` so they never collide with
   the Kalles theme scripts. Functions stay global because the section markup
   calls them through inline onclick handlers.

   v20 updates:
   - The merchant now binds ONE battery collection list holding 12V / 24V /
     48V batteries together. The voltage tier is picked from the calculated
     kWh (scPickVoltage: 12V <= 5 kWh, 24V <= 12 kWh, 48V above - adjust
     scVoltageTiers to match the catalogue) and only batteries whose voltage
     matches that tier are ranked (scVoltageMatches; products without a
     voltage stay candidates). scBatterySpecs gained the 24V default.
   - The bound JSON key for batteries changed from "batteries12v" +
     "batteries48v" to a single "batteries" array.
   - Product images may fall back to the product's first image / media cover
     (handled in Liquid; the JS still shows a placeholder when no image).

   v2 updates:
   - Appliances now have quantity + daily hours controls.
   - Recommended products are read from the section's `#sc-product-data`
     JSON block, so merchants can bind them in the theme editor.
   - Battery recommendation quantity is auto-calculated from required kWh.

   v3 updates:
   - The merchant can bind MULTIPLE products per group via product_list:
       batteries12v / batteries48v / inverters
   - Results are rendered in two groups: "Battery Storage" (voltage-based,
     each card shows its own recommended qty) and "Hybrid Inverter"
     (always shown, qty 1 per unit).

   v4 updates:
   - Complete visual redesign (see section + CSS). New appliance card markup
     uses .appliance-head for icon / title / checkbox.
   - Live load summary (#live-devices / #live-kwh) is updated whenever the
     appliance list re-renders.
   - #btn-restart is shown on the results step so visitors can start over.

   v5 updates:
   - Manual navigation: selecting a scenario or subtype NO LONGER jumps to
     the next step automatically. Visitors pick a card, then press Next.
   - Next is disabled until a scenario (step 1) / subtype (step 2) is chosen,
     and pulses once when a valid selection is made.
   - More scenarios (6) and more subtypes per scenario (5-6 each):
       home / rv / van / house / camping  (Home Storage listed first)

   v6 updates:
   - Appliances expanded to ~40 devices grouped into categories with a
     "Common Essentials" / "Clear all" toolbar (see scApplianceCats).
   - Product recommendations are now bound by COLLECTION (collection_list in
     the section schema) instead of individual products, plus a NEW "combo
     kit" group. Each product may carry capacity / power metafields
     (custom.capacity_kwh, custom.power_w, custom.voltage); JS falls back to
     parsing the title/tags when they are missing.
   - Dynamic auto-recommendation: results are classified into a tier
     (Portable & Compact / 12V System / 48V System), combos are ranked by how
     closely their capacity covers the calculated battery size (Best Match
     badge), batteries and inverters are ranked by capacity/power match and
     only the top 3 of each group are shown, each with a "why this fits" note.

   v7 updates:
   - Appliance categories trimmed to five with title-case labels only:
       Kitchen & Cooking / Refrigeration & Cooling / Climate & Comfort /
       Home & Water / Outdoor & Mobile  (no all-caps, no "Electronics" group;
       electronics devices moved into Home & Water / Outdoor & Mobile).
   - Devices are now SCENE-AWARE: each appliance carries a `scenes` array
       (home / rv / van / house / camping). The Devices step only
       shows appliances that fit the setup chosen on Steps 1-2, and picking a
       scenario resets the default selection to scSceneDefaults for that
       scenario (scApplySceneDefaults).
   - All text uses the Poppins font family (loaded in the section).

   v8 updates:
   - SETUP-AWARE devices: each subtype in scSubtypeRules may hide appliances
     that do not fit that specific setup and/or override the default
     selection (scApplySubtypeDefaults). Picking a different setup on Step 2
     therefore changes both the visible device list and the pre-selected
     devices, instead of showing the same list for every subtype. Switching
     scenario clears the subtype so stale rules never leak across scenarios.
   - CUSTOM DEVICES: visitors can add their own appliance (name + watts +
     hours) through the form on the Devices step. Custom devices are stored
     in scState.appliances with cat:'custom', rendered under a "Custom
     Devices" category, counted by the live summary, included in the
     calculation and removable via the x button. They survive scenario
     switches and are cleared on Reset.

   v9 updates:
   - The "Add your own device" form moved to the BOTTOM of the Devices step
     (below the appliance list) so the preset device cards come first.
   - The Result step now shows exactly ONE pick per category - the best
     battery, the best hybrid inverter and the best complete kit - laid out
     HORIZONTALLY (Battery Storage | Hybrid Inverter | Complete Kit) instead
     of three stacked groups of up to three cards.

   v10 updates:
   - The Complete Kit card buttons now match the other two groups exactly
     ("Add to Cart" / "Buy Now" - no more "Add Kit to Cart" / "Buy Kit").
   - Every English word in the generated recommendation notes / badges is
     title-cased (first letter of each word capitalized) while unit tokens
     like kWh / 12V / W stay intact (scTitleCase).
   - Recommendations are now VARIANT-AWARE: products bound from a collection
     may ship multiple variants, each with its own capacity / power /
     voltage (variant metafields, or parsed from the variant title / option
     values, falling back to the product-level values). The engine picks the
     single best-matching variant per product, shows it on the card and adds
     THAT variant's id to the cart, so the recommendation really follows the
     parameters of the products inside the bound collections.

   v11 updates:
   - Devices are NO LONGER pre-selected. Picking a scenario or a setup no
     longer ticks any appliances by default - the Devices step starts with
     every device unticked (0/N per category) and the visitor checks the
     devices they actually use. The legacy default lists (scSceneDefaults
     and the `defaults` arrays in scSubtypeRules) are kept as reference
     only and are never applied anymore. The setup `hide` rules still
     filter which devices are shown, and the "Common Essentials" toolbar
     button is still there for a one-tap starting point.
   v12 updates:
   - Bound products now have a FOURTH group: All-in-One (aio) energy
     stations / power stations from a separate collection_list setting.
     The Result step now shows FOUR horizontal columns side by side
     instead of three:
         Battery Storage | Hybrid Inverter | Complete Kit | All-in-One
     The all-in-one column is recommended from the calculated load the
     same way as the kit column (best capacity match, single pick, Best
     Match badge when it covers the need with qty 1).
   v13 updates (no logic change - visual polish lives in the CSS/HTML):
   - Wide-layout optimization for the 1440px container: the six scenario
     tiles sit in one row, setup cards use three columns, device cards
     are wider, the result summary becomes a 4-up KPI row on desktop,
     step headers are centered and hero/body paddings scale up on
     large screens.
   - All six scenario icons were redrawn as cleaner duotone line icons
     (soft translucent fill accents, per-scenario colored glow).
   ========================================================================== */

// State
let scCurrentStep = 1;
let scState = {
  scenario: null,
  subtype: null,
  customSubtypeName: '',
  customScenarioName: '',
  appliances: [
    // --- Kitchen & Cooking ---
    { id: 'microwave',      cat: 'kitchen',  name: 'Microwave',              icon: '🍕', power: 1200, hours: 0.5, qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'stove',          cat: 'kitchen',  name: 'Electric Stove',         icon: '🍳', power: 3000, hours: 1,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'oven',           cat: 'kitchen',  name: 'Oven',                   icon: '🥘', power: 3000, hours: 1,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'coffee-maker',   cat: 'kitchen',  name: 'Coffee Maker',           icon: '☕', power: 800,  hours: 0.5, qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'kettle',         cat: 'kitchen',  name: 'Electric Kettle',        icon: '⚗️', power: 1500, hours: 0.3, qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'air-fryer',      cat: 'kitchen',  name: 'Air Fryer',              icon: '🍟', power: 1500, hours: 0.5, qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'instant-pot',    cat: 'kitchen',  name: 'Instant Pot / Slow Cooker', icon: '🥣', power: 900, hours: 1, qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'dishwasher',     cat: 'kitchen',  name: 'Dishwasher',             icon: '🍽️', power: 1800, hours: 1,   qty: 1, selected: false, scenes: ['house','home'] },
    // --- Refrigeration & Cooling ---
    { id: 'refrigerator',   cat: 'cooling',  name: 'Refrigerator',           icon: '🧊', power: 150,  hours: 6,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'chest-freezer',  cat: 'cooling',  name: 'Chest Freezer',          icon: '❄️', power: 250,  hours: 8,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'portable-fridge',cat: 'cooling',  name: 'Portable 12V Fridge',    icon: '🧺', power: 40,   hours: 24,  qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'air-conditioner',cat: 'cooling',  name: 'Air Conditioner',        icon: '❄️', power: 2000, hours: 4,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    // --- Climate & Comfort ---
    { id: 'heater',         cat: 'climate',  name: 'Heater',                 icon: '🔥', power: 2000, hours: 4,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'ceiling-fan',    cat: 'climate',  name: 'Ceiling Fan',            icon: '🌀', power: 50,   hours: 6,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'electric-blanket', cat: 'climate', name: 'Electric Blanket',      icon: '🛏️', power: 100,  hours: 8,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'heat-pump',      cat: 'climate',  name: 'Heat Pump',              icon: '🌡️', power: 2500, hours: 4,   qty: 1, selected: false, scenes: ['house','home'] },
    { id: 'dehumidifier',   cat: 'climate',  name: 'Dehumidifier',           icon: '🌫️', power: 500,  hours: 6,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'exhaust',        cat: 'climate',  name: 'Bathroom Exhaust Fan',   icon: '💨', power: 100,  hours: 2,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'water-heater',   cat: 'climate',  name: 'Water Heater',           icon: '🚿', power: 4000, hours: 2,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    // --- Home & Water ---
    { id: 'lights',         cat: 'home',     name: 'Lighting (LED)',         icon: '💡', power: 100,  hours: 4,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'tv',             cat: 'home',     name: 'TV',                     icon: '📺', power: 150,  hours: 4,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'router',         cat: 'home',     name: 'Internet Router',        icon: '🌐', power: 20,   hours: 24,  qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'security',       cat: 'home',     name: 'Security System',        icon: '🔒', power: 30,   hours: 24,  qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'computer',       cat: 'home',     name: 'Computer / Home Office', icon: '💻', power: 200,  hours: 4,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'washing',        cat: 'home',     name: 'Washing Machine',        icon: '🧺', power: 500,  hours: 1,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'dryer',          cat: 'home',     name: 'Clothes Dryer',          icon: '👕', power: 4000, hours: 1,   qty: 1, selected: false, scenes: ['house','home'] },
    { id: 'well-pump',      cat: 'home',     name: 'Well Pump',              icon: '💧', power: 500,  hours: 1,   qty: 1, selected: false, scenes: ['house','home'] },
    { id: 'sump-pump',      cat: 'home',     name: 'Sump Pump',              icon: '🛟', power: 700,  hours: 1,   qty: 1, selected: false, scenes: ['rv','van','house','home'] },
    { id: 'septic',         cat: 'home',     name: 'Septic System',          icon: '🚰', power: 100,  hours: 2,   qty: 1, selected: false, scenes: ['house','home'] },
    { id: 'irrigation',     cat: 'home',     name: 'Irrigation System',      icon: '🌱', power: 800,  hours: 1,   qty: 1, selected: false, scenes: ['house','home'] },
    // --- Outdoor & Mobile ---
    { id: 'laptop',         cat: 'outdoor',  name: 'Laptop',                 icon: '💼', power: 65,   hours: 6,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'phone',          cat: 'outdoor',  name: 'Phone Charger',          icon: '📱', power: 10,   hours: 6,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'gaming',         cat: 'outdoor',  name: 'Gaming Console',         icon: '🎮', power: 200,  hours: 2,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'sound',          cat: 'outdoor',  name: 'Sound System',           icon: '🔊', power: 100,  hours: 3,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'power-tools',    cat: 'outdoor',  name: 'Power Tools',            icon: '🔧', power: 800,  hours: 1,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'ev-charger',     cat: 'outdoor',  name: 'EV Charger',             icon: '🚗', power: 3000, hours: 2,   qty: 1, selected: false, scenes: ['house','home'] },
    { id: 'cpap',           cat: 'outdoor',  name: 'CPAP / Medical Device',  icon: '💓', power: 60,   hours: 8,   qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] },
    { id: 'hair-dryer',     cat: 'outdoor',  name: 'Hair Dryer',             icon: '💇', power: 1500, hours: 0.3, qty: 1, selected: false, scenes: ['rv','van','house','home','camping'] }
  ]
};

// v7: five categories, title-case labels only (no all-caps, no Electronics group)
// v8: + 'custom' category, rendered last, only when the visitor added devices
const scApplianceCats = [
  { id: 'kitchen',  label: 'Kitchen & Cooking' },
  { id: 'cooling',  label: 'Refrigeration & Cooling' },
  { id: 'climate',  label: 'Climate & Comfort' },
  { id: 'home',     label: 'Home & Water' },
  { id: 'outdoor',  label: 'Outdoor & Mobile' },
  { id: 'custom',   label: 'Custom Devices' }
];

// v7: per-scenario default selections. v11: KEPT FOR REFERENCE ONLY - never
// applied anymore. Since v11 the Devices step always starts with nothing
// selected; visitors tick the devices they actually use (or tap the
// "Common Essentials" toolbar button for a one-tap starting point).
const scSceneDefaults = {
  rv:      ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker', 'ceiling-fan'],
  van:     ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'microwave', 'ceiling-fan'],
  house:   ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'water-heater', 'ceiling-fan'],
  home:    ['refrigerator', 'lights', 'router', 'tv', 'phone', 'computer', 'sump-pump', 'laptop'],
  camping: ['portable-fridge', 'lights', 'phone', 'laptop', 'coffee-maker', 'kettle', 'sound'],
  custom: ['lights', 'router', 'laptop', 'phone', 'coffee-maker', 'portable-fridge', 'power-tools', 'cpap']
};

// v8: per-setup rules. Each subtype may:
//   - `hide`:    appliance ids that do NOT fit this specific setup
//                (these are hidden on the Devices step even if the device
//                belongs to the scenario)
//   - `defaults`: v11: LEGACY / REFERENCE ONLY - never applied anymore.
//                Devices are never pre-selected since v11; visitors tick
//                the devices they actually use.
// Every subtype hides something different, so the Devices step is never
// identical between two setups of the same scenario.
const scSubtypeRules = {
  // ---- RV & Camper Van ----
  'class-a':        { hide: [], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker', 'ceiling-fan', 'stove', 'water-heater'] },
  'class-b':        { hide: ['dishwasher', 'dryer', 'well-pump', 'irrigation', 'septic'] },
  'class-c':        { hide: ['dryer', 'well-pump', 'irrigation', 'septic'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker', 'ceiling-fan', 'stove'] },
  'fifth-wheel':    { hide: ['well-pump', 'irrigation', 'septic'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker', 'ceiling-fan', 'oven', 'washing'] },
  'truck-camper':   { hide: ['dishwasher', 'dryer', 'microwave', 'stove', 'oven', 'ceiling-fan', 'water-heater', 'well-pump', 'irrigation', 'septic', 'air-conditioner', 'dehumidifier', 'washing', 'computer'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'portable-fridge', 'electric-blanket'] },
  'pop-up':         { hide: ['microwave', 'stove', 'oven', 'dishwasher', 'dryer', 'ceiling-fan', 'water-heater', 'well-pump', 'irrigation', 'septic', 'air-conditioner', 'dehumidifier', 'washing', 'computer', 'tv'], defaults: ['portable-fridge', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'kettle', 'electric-blanket'] },
  // ---- Van & Bus Conversion ----
  'sprinter':       { hide: ['dishwasher', 'dryer', 'well-pump', 'irrigation', 'septic'] },
  'short-wheelbase':{ hide: ['dishwasher', 'dryer', 'microwave', 'water-heater', 'well-pump', 'irrigation', 'septic', 'washing'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'ceiling-fan'] },
  'minivan':        { hide: ['dishwasher', 'dryer', 'microwave', 'stove', 'oven', 'water-heater', 'well-pump', 'irrigation', 'septic', 'washing', 'computer'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'portable-fridge'] },
  'overland-van':   { hide: ['dishwasher', 'dryer', 'well-pump', 'irrigation', 'septic', 'washing'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'microwave', 'power-tools', 'electric-blanket'] },
  'skoolie':        { hide: ['well-pump', 'irrigation', 'septic'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker', 'ceiling-fan', 'washing', 'water-heater'] },
  'cargo':          { hide: ['dishwasher', 'dryer', 'microwave', 'stove', 'oven', 'ceiling-fan', 'water-heater', 'well-pump', 'irrigation', 'septic', 'washing', 'tv'], defaults: ['lights', 'router', 'phone', 'laptop', 'power-tools', 'coffee-maker', 'heater'] },
  // ---- Tiny House & Cabin ----
  'cabin':          { hide: ['dryer', 'irrigation'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'water-heater', 'ceiling-fan', 'stove'] },
  'tiny-home':      { hide: ['dryer', 'dishwasher', 'irrigation'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'ceiling-fan'] },
  'container':      { hide: ['irrigation', 'septic'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'water-heater', 'washing', 'sump-pump'] },
  'shed':           { hide: ['dishwasher', 'dryer', 'well-pump', 'irrigation', 'septic', 'water-heater', 'ceiling-fan', 'tv', 'washing', 'heat-pump', 'air-conditioner', 'microwave'], defaults: ['lights', 'router', 'phone', 'power-tools', 'heater', 'computer', 'coffee-maker'] },
  'yurt':           { hide: ['dishwasher', 'dryer', 'washing', 'well-pump', 'irrigation', 'septic', 'microwave', 'stove', 'oven', 'water-heater', 'heat-pump', 'air-conditioner', 'tv'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'electric-blanket', 'heater'] },
  'treehouse':      { hide: ['dishwasher', 'dryer', 'washing', 'well-pump', 'irrigation', 'septic', 'microwave', 'stove', 'oven', 'water-heater', 'heat-pump', 'air-conditioner', 'tv', 'ceiling-fan'], defaults: ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'coffee-maker', 'kettle', 'electric-blanket'] },
  // ---- Home Storage ----
  'whole-home':     { hide: [], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'computer', 'sump-pump', 'laptop', 'water-heater', 'air-conditioner', 'microwave'] },
  'essential':      { hide: ['dryer', 'well-pump', 'irrigation', 'dishwasher', 'stove', 'oven', 'water-heater', 'heat-pump', 'ev-charger', 'air-conditioner', 'washing', 'septic', 'sump-pump', 'computer', 'gaming', 'microwave', 'hair-dryer', 'dehumidifier'], defaults: ['refrigerator', 'lights', 'router', 'phone'] },
  'apartment':      { hide: ['well-pump', 'irrigation', 'septic', 'dryer', 'sump-pump'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'coffee-maker'] },
  'home-office':    { hide: ['well-pump', 'irrigation', 'septic', 'dryer', 'dishwasher', 'washing'], defaults: ['refrigerator', 'lights', 'router', 'computer', 'laptop', 'phone', 'tv', 'microwave', 'coffee-maker'] },
  'vacation-rental':{ hide: ['well-pump', 'irrigation', 'septic'], defaults: ['refrigerator', 'lights', 'router', 'tv', 'phone', 'laptop', 'microwave', 'water-heater', 'washing', 'coffee-maker'] },
  // ---- Camping & Outdoor ----
  'overland':       { hide: ['microwave', 'stove', 'oven', 'dishwasher', 'dryer', 'washing', 'water-heater', 'ceiling-fan', 'dehumidifier', 'heat-pump', 'well-pump', 'irrigation', 'septic', 'ev-charger', 'air-conditioner', 'tv', 'computer', 'gaming'], defaults: ['portable-fridge', 'lights', 'phone', 'laptop', 'coffee-maker', 'kettle', 'electric-blanket', 'power-tools'] },
  'glamping':       { hide: ['dryer', 'washing', 'well-pump', 'irrigation', 'septic', 'ev-charger'], defaults: ['portable-fridge', 'lights', 'phone', 'laptop', 'coffee-maker', 'kettle', 'sound', 'electric-blanket', 'heater', 'tv'] },
  'tailgate':       { hide: ['microwave', 'stove', 'oven', 'dishwasher', 'dryer', 'washing', 'water-heater', 'ceiling-fan', 'dehumidifier', 'heat-pump', 'well-pump', 'irrigation', 'septic', 'ev-charger', 'air-conditioner', 'tv', 'computer', 'gaming', 'hair-dryer', 'cpap'], defaults: ['portable-fridge', 'lights', 'phone', 'sound', 'laptop', 'coffee-maker', 'kettle'] },
  'backyard':       { hide: ['well-pump', 'irrigation', 'septic', 'ev-charger', 'dryer', 'washing'], defaults: ['refrigerator', 'lights', 'phone', 'sound', 'portable-fridge', 'laptop', 'tv', 'coffee-maker'] },
  'festival':       { hide: ['microwave', 'stove', 'oven', 'dishwasher', 'dryer', 'washing', 'water-heater', 'ceiling-fan', 'dehumidifier', 'heat-pump', 'well-pump', 'irrigation', 'septic', 'ev-charger', 'air-conditioner', 'tv', 'computer', 'gaming', 'hair-dryer'], defaults: ['portable-fridge', 'lights', 'phone', 'laptop', 'coffee-maker', 'kettle', 'sound', 'electric-blanket'] }
};

const scSubtypes = {
  home: [
    { id: 'whole-home', name: 'Whole-Home Backup', desc: 'Keep the essentials running during outages' },
    { id: 'essential', name: 'Essential Loads Only', desc: 'Fridge, lights and internet during blackouts' },
    { id: 'apartment', name: 'Apartment / Flat', desc: 'Compact backup for city living' },
    { id: 'home-office', name: 'Home Office / Studio', desc: 'Power for workstations and equipment' },
    { id: 'vacation-rental', name: 'Vacation Rental / Airbnb', desc: 'Reliable power for guests' }
  ],
  rv: [
    { id: 'class-a', name: 'Class A Motorhome', desc: 'Large motorhome with high power needs' },
    { id: 'class-b', name: 'Class B Camper Van', desc: 'Compact factory-built camper van' },
    { id: 'class-c', name: 'Class C / Travel Trailer', desc: 'Towable trailer or family RV' },
    { id: 'fifth-wheel', name: 'Fifth Wheel', desc: 'Large towable with full home amenities' },
    { id: 'truck-camper', name: 'Truck Camper', desc: 'Slide-in pickup bed camper' },
    { id: 'pop-up', name: 'Pop-Up Camper', desc: 'Lightweight folding trailer' }
  ],
  van: [
    { id: 'sprinter', name: 'Sprinter / Full-Size Van', desc: 'Mercedes, Ford Transit or RAM ProMaster build' },
    { id: 'short-wheelbase', name: 'Short Wheelbase Van', desc: 'Compact cargo van for weekend trips' },
    { id: 'minivan', name: 'Minivan Conversion', desc: 'Compact weekender or commuter conversion' },
    { id: 'overland-van', name: 'Off-Road / Overland Van', desc: '4x4 expedition van with rooftop gear' },
    { id: 'skoolie', name: 'Skoolie / Bus', desc: 'Converted school bus or coach' },
    { id: 'cargo', name: 'Cargo / Utility Trailer', desc: 'Work trailer, mobile studio or hauler' }
  ],
  house: [
    { id: 'cabin', name: 'Off-Grid Cabin', desc: 'Remote woodland or mountain cabin' },
    { id: 'tiny-home', name: 'Tiny House', desc: 'Mobile tiny home on wheels' },
    { id: 'container', name: 'Container Home', desc: 'Converted shipping container dwelling' },
    { id: 'shed', name: 'Workshop / Shed', desc: 'Small outbuilding power setup' },
    { id: 'yurt', name: 'Yurt / Glamping Unit', desc: 'Round shelter or rental unit' },
    { id: 'treehouse', name: 'Treehouse / Retreat', desc: 'Elevated hideaway off the grid' }
  ],
  camping: [
    { id: 'overland', name: 'Overland / 4x4', desc: 'Roof tent or truck bed expedition setup' },
    { id: 'glamping', name: 'Glamping', desc: 'Comfortable off-grid camp with extras' },
    { id: 'tailgate', name: 'Tailgating / Events', desc: 'Portable power for the tailgate party' },
    { id: 'backyard', name: 'Backyard / Garden', desc: 'Patio parties and outdoor gatherings' },
    { id: 'festival', name: 'Festival / Weekend Camp', desc: 'Multi-day camping with gadgets' }
  ],
  custom: []
};

// Capacity assumptions used to recommend battery quantity. These are only
// fallback defaults (placeholder card) - real products use their own
// capacity_kwh metafield / parsed title values.
// v20: the merchant binds ONE battery list (12V / 24V / 48V together), so
// the 24V default was added.
const scBatterySpecs = {
  '12V': { voltage: 12, ah: 100, kwh: 1.2 },   // 12V 100Ah ≈ 1.2kWh
  '24V': { voltage: 24, ah: 100, kwh: 2.56 },  // 24V 100Ah (LiFePO4 25.6V) ≈ 2.56kWh
  '48V': { voltage: 48, ah: 314, kwh: 15.07 }   // 48V 314Ah ≈ 15.07kWh
};

// v20: voltage tiers used to pick the system voltage from the calculated
// battery need. Adjust the maxKwh boundaries to match the product catalogue.
const scVoltageTiers = [
  { maxKwh: 5,  voltage: '12V' },
  { maxKwh: 12, voltage: '24V' },
  { maxKwh: Infinity, voltage: '48V' }
];

function scPickVoltage(batteryKwh) {
  for (let i = 0; i < scVoltageTiers.length; i++) {
    if (batteryKwh <= scVoltageTiers[i].maxKwh) return scVoltageTiers[i].voltage;
  }
  return '48V';
}

// ---------------------------------------------------------------------------
// v24: scenario-aware product filtering.
//
// Tag convention (set on the product in Shopify admin):
//   scene:home     -> only recommended for "Home Storage"
//   scene:rv       -> only recommended for "RV & Camper Van"
//   scene:van      -> only recommended for "Van & Bus Conversion"
//   scene:cabin   -> only recommended for "Tiny House & Cabin"
//   scene:cabin    -> only recommended for "Tiny House & Cabin"
//   scene:camping  -> only recommended for "Camping & Outdoor"
// (`scene-xxx` and `scene_xxx` separators work too.)
//
// Products WITHOUT any scene: tag are UNIVERSAL - they stay candidates for
// every scenario, so existing bindings keep working unchanged.
// Ranking order per pool: scenario-tagged products first, universal products
// after. If nothing is tagged for the active scenario the universal pool is
// used on its own, so a pool is only ever empty when the merchant bound no
// suitable products at all.
// ---------------------------------------------------------------------------
const scScenarioTagKeys = {
  home:    ['home', 'house-storage', 'homestorage'],
  rv:      ['rv', 'camper', 'campervan', 'camper-van', 'motorhome'],
  van:     ['van', 'bus', 'vanlife', 'van-life', 'conversion'],
  house:   ['cabin', 'tinyhouse', 'tiny-house', 'tiny', 'offgrid', 'off-grid'],
  camping: ['camping', 'outdoor', 'tent', 'weekend']
};

function scProductSceneKeys(p) {
  return (p.tags || [])
    .map(t => String(t).trim().toLowerCase().replace(/\s+/g, '-'))
    .filter(t => /^scene[:_-]/.test(t))
    .map(t => t.replace(/^scene[:_-]/, ''));
}

// v25: per-pool scenario hard rules that go beyond the scene: tag system.
// Camping demands portable gear - whole-home All-in-One stations are too
// bulky and not weather-rated. Merchants can extend this object when more
// rules are needed without touching the rest of the engine.
const scScenarioHardExclusions = {
  // 'poolName': Set of scenario ids that should never see this pool
  allInOne: new Set(['camping'])
};

function scApplyScenarioHardRules(poolName, list, scenario) {
  const exclude = scScenarioHardExclusions[poolName];
  if (!exclude || !scenario || !Array.isArray(list) || !list.length) return list;
  if (exclude.has(scenario)) return [];
  return list;
}

// v26: strict scenarios ignore the universal (untagged) fallback.
// Only products explicitly tagged scene:<scenario> are recommended. If
// the merchant hasn't tagged anything for the active strict scenario,
// the pool is empty (no placeholders, no universal replacements). This
// lets "Camping & Outdoor" stay curated even when the merchant has
// many untagged batteries in the bound collection.
const scStrictScenarios = new Set(['camping']);

function scFilterByScenario(list, scenario) {
  if (!scenario || !Array.isArray(list) || !list.length) return list || [];
  const keys = scScenarioTagKeys[scenario] || [String(scenario).toLowerCase()];
  const tagged = [];
  const universal = [];
  const strict = scStrictScenarios.has(scenario);
  list.forEach(p => {
    const pk = scProductSceneKeys(p);
    if (pk.length === 0) {
      if (!strict) universal.push(p);
    } else if (pk.some(k => keys.indexOf(k) !== -1)) {
      tagged.push(p);
    }
    // products tagged ONLY for other scenarios are excluded
  });
  return tagged.concat(universal);
}

// v20: does a battery belong to the target voltage tier? 12.8V / 12V
// batteries live in the 12V tier, 25.6V / 24V in the 24V tier and 48V+
// in the 48V tier. Products without a voltage stay candidates so legacy
// products (no metafield, unparsable title) still get recommended.
function scVoltageMatches(productVoltage, target) {
  if (!productVoltage) return true;
  const n = parseFloat(String(productVoltage));
  if (!isFinite(n)) return true;
  if (n >= 48) return target === '48V';
  if (n >= 24) return target === '24V';
  return target === '12V';
}

function scSelectScenario(scenarioId, element) {
  scState.scenario = scenarioId;
  // v8: a setup chosen for the previous scenario must not leak into the new
  // one - the visitor picks a fresh setup on Step 2.
  scState.subtype = scenarioId === 'custom' ? 'custom-setup' : null;
  document.querySelectorAll('.hdt-solar-calc .scenario-card').forEach(el => el.classList.remove('selected'));
  element.classList.add('selected');

  // v11: no default selection - every preset device starts unticked for this
  // scenario (visitor-added custom devices keep their state).
  scClearPresetSelection();

  // v5: manual navigation - do NOT jump to the next step automatically.
  scUpdateView();
  scFlashNext();
}

function scUpdateCustomScenarioName(value, event) {
  if (event) event.stopPropagation();
  scState.customScenarioName = String(value || '').trim().slice(0, 40);
}

// v11: clears the selection of all preset devices (custom devices keep
// theirs - they belong to the visitor, not to a scenario/setup). Called
// whenever the visitor picks a new scenario or setup, so the Devices step
// always starts unticked instead of inheriting a previous selection.
function scClearPresetSelection() {
  scState.appliances.forEach(app => {
    if (!app.custom) {
      app.selected = false;
      app.qty = 1;
    }
  });
}

function scSelectSubtype(subtypeId, element) {
  scState.subtype = subtypeId;
  document.querySelectorAll('#subtype-container .scenario-card').forEach(el => el.classList.remove('selected'));
  element.classList.add('selected');

  // v11: no default selection - changing the setup clears the preset ticks
  // so the Devices step starts unticked for THIS setup (the subtype `hide`
  // rules still control which devices are shown).
  scClearPresetSelection();

  // v5: manual navigation - do NOT jump to the next step automatically.
  scUpdateView();
  scFlashNext();
}

// Briefly pulses the Next button so the visitor notices it is now enabled.
function scFlashNext() {
  const btn = document.getElementById('btn-next');
  if (!btn || btn.disabled) return;
  btn.classList.remove('flash');
  void btn.offsetWidth; // restart the CSS animation
  btn.classList.add('flash');
}

function scToggleAppliance(id) {
  const item = scState.appliances.find(a => a.id === id);
  if (item) {
    item.selected = !item.selected;
    scRenderAppliances();
  }
}

function scUpdateApplianceQty(id, delta) {
  const item = scState.appliances.find(a => a.id === id);
  if (!item) return;
  item.qty = Math.max(1, Math.min(99, item.qty + delta));
  if (!item.selected) item.selected = true;
  scRenderAppliances();
}

function scSetApplianceQty(id, value) {
  const item = scState.appliances.find(a => a.id === id);
  if (!item) return;
  const n = parseInt(value, 10);
  item.qty = isNaN(n) ? 1 : Math.max(1, Math.min(99, n));
  if (!item.selected) item.selected = true;
  scRenderAppliances();
}

function scUpdateApplianceHours(id, delta) {
  const item = scState.appliances.find(a => a.id === id);
  if (!item) return;
  item.hours = Math.max(0.5, Math.min(24, Math.round((item.hours + delta) * 2) / 2));
  if (!item.selected) item.selected = true;
  scRenderAppliances();
}

function scSetApplianceHours(id, value) {
  const item = scState.appliances.find(a => a.id === id);
  if (!item) return;
  const n = parseFloat(value);
  item.hours = isNaN(n) ? 1 : Math.max(0.5, Math.min(24, n));
  if (!item.selected) item.selected = true;
  scRenderAppliances();
}

function scRenderSubtypes() {
  const container = document.getElementById('subtype-container');
  const list = scSubtypes[scState.scenario] || scSubtypes.rv;
  const customCard = `
    <div class="scenario-card sc-custom-setup-card ${scState.subtype === 'custom-setup' ? 'selected' : ''}" onclick="scSelectSubtype('custom-setup', this)">
      <h3>Custom Setup</h3>
      <p>Describe your own setup and continue with a flexible device list.</p>
      <input type="text" class="sc-custom-setup-input" value="${scState.customSubtypeName || ''}" placeholder="e.g. Mobile Workshop" maxlength="40" onclick="event.stopPropagation()" onchange="scState.customSubtypeName = this.value.trim()">
    </div>`;
  container.innerHTML = list.map(item => `
    <div class="scenario-card ${scState.subtype === item.id ? 'selected' : ''}" onclick="scSelectSubtype('${item.id}', this)">
      <h3>${item.name}</h3>
      <p>${item.desc}</p>
      </div>
  `).join('') + customCard;
}

// Keep the device picker useful at a glance: each scenario exposes a short,
// editable starter list instead of the entire appliance catalogue. Visitors can
// still add any extra device with the custom-device form below.
const scSceneDeviceIds = {
  home: ['refrigerator', 'lights', 'router', 'tv', 'computer', 'washing', 'microwave', 'coffee-maker'],
  rv: ['portable-fridge', 'lights', 'laptop', 'phone', 'coffee-maker', 'heater', 'refrigerator', 'microwave'],
  van: ['portable-fridge', 'lights', 'laptop', 'phone', 'coffee-maker', 'power-tools', 'refrigerator', 'electric-blanket'],
  house: ['refrigerator', 'lights', 'router', 'tv', 'washing', 'water-heater', 'microwave', 'coffee-maker'],
  camping: ['portable-fridge', 'lights', 'phone', 'laptop', 'coffee-maker', 'cpap', 'sound', 'power-tools']
};

function scRenderAppliances() {
  const container = document.getElementById('appliances-container');
  const scene = scState.scenario;
  const rules = (scState.subtype && scSubtypeRules[scState.subtype]) || {};
  const hidden = rules.hide || [];
  const starterIds = scSceneDeviceIds[scene] || [];
  const items = scState.appliances.filter(a =>
    (a.custom || starterIds.indexOf(a.id) !== -1) &&
    (a.custom && scene === 'custom' || !a.scenes || a.scenes.indexOf(scene) !== -1)
  );
  const selected = items.filter(i => i.selected).length;
  container.innerHTML = `
    <div class="sc-device-picker-head">
      <span class="sc-appliance-cat-label">Suggested Devices</span>
      <span class="sc-appliance-cat-count">${selected}/${items.length}</span>
    </div>
    <div class="sc-appliance-row sc-device-picker-row">
      ${items.map(app => scApplianceCardHtml(app)).join('')}
    </div>`;
  scUpdateLiveSummary();
}

function scApplianceCardHtml(app) {
  // v8: custom devices get a remove button so visitors can delete them again.
  const removeBtn = app.custom
    ? `<button type="button" class="appliance-remove" onclick="scRemoveAppliance('${app.id}'); event.stopPropagation();" aria-label="Remove ${app.name.replace(/"/g, '&quot;')}">&times;</button>`
    : '';
  return `
    <div class="appliance-item ${app.selected ? 'selected' : ''} ${app.custom ? 'custom' : ''}" onclick="scToggleAppliance('${app.id}')">
      ${removeBtn}
      <div class="appliance-head">
        <span class="appliance-icon">${app.icon}</span>
        <span class="appliance-title">${app.name}</span>
        <span class="appliance-checkbox">${app.selected ? '✓' : ''}</span>
      </div>
      <div class="appliance-power">${app.power}W per unit</div>
      <div class="appliance-controls" onclick="event.stopPropagation()">
        <div class="control-group">
          <span class="control-label">Qty</span>
          <button type="button" class="control-btn" onclick="scUpdateApplianceQty('${app.id}', -1); event.stopPropagation();">-</button>
          <input type="number" class="control-input" value="${app.qty}" min="1" max="99" onchange="scSetApplianceQty('${app.id}', this.value); event.stopPropagation();" onclick="event.stopPropagation();">
          <button type="button" class="control-btn" onclick="scUpdateApplianceQty('${app.id}', 1); event.stopPropagation();">+</button>
        </div>
        <div class="control-group">
          <span class="control-label">Hours</span>
          <button type="button" class="control-btn" onclick="scUpdateApplianceHours('${app.id}', -0.5); event.stopPropagation();">-</button>
          <input type="number" class="control-input hours" value="${app.hours}" min="0.5" max="24" step="0.5" onchange="scSetApplianceHours('${app.id}', this.value); event.stopPropagation();" onclick="event.stopPropagation();">
          <button type="button" class="control-btn" onclick="scUpdateApplianceHours('${app.id}', 0.5); event.stopPropagation();">+</button>
        </div>
      </div>
    </div>`;
}

// v8: adds a visitor-defined appliance from the "Add your own device" form.
let scCustomSeq = 0;
function scAddCustomAppliance(event) {
  if (event && event.preventDefault) event.preventDefault();
  const nameEl = document.getElementById('sc-custom-name');
  const powerEl = document.getElementById('sc-custom-power');
  const hoursEl = document.getElementById('sc-custom-hours');
  if (!nameEl || !powerEl) return;

  const name = (nameEl.value || '').trim();
  const power = parseFloat(powerEl.value);
  if (!name || isNaN(power) || power <= 0) return;

  scCustomSeq++;
  scState.appliances.push({
    id: 'custom-' + scCustomSeq + '-' + Date.now(),
    cat: 'custom',
    name: name.slice(0, 40),
    icon: '🔌',
    power: power,
    hours: hoursEl ? parseFloat(hoursEl.value) : 1,
    qty: 1,
    selected: true,
    custom: true,
    scenes: ['custom', 'rv', 'van', 'house', 'home', 'camping']
  });
  // clamp hours to a sane 0.5-24 range
  const item = scState.appliances[scState.appliances.length - 1];
  if (isNaN(item.hours) || item.hours <= 0) item.hours = 1;
  item.hours = Math.max(0.5, Math.min(24, item.hours));

  nameEl.value = '';
  powerEl.value = '';
  if (hoursEl) hoursEl.value = '1';

  scRenderAppliances();
}

function scRemoveAppliance(id) {
  const idx = scState.appliances.findIndex(a => a.id === id && a.custom);
  if (idx === -1) return;
  scState.appliances.splice(idx, 1);
  scRenderAppliances();
}

// v6: one-tap preset selection / clear-all toolbar actions.
function scSelectEssentials() {
  const essentials = ['refrigerator', 'lights', 'router', 'phone', 'laptop', 'ceiling-fan', 'coffee-maker'];
  scState.appliances.forEach(app => {
    app.selected = essentials.includes(app.id);
  });
  scRenderAppliances();
}

function scClearAppliances() {
  scState.appliances.forEach(app => { app.selected = false; });
  scRenderAppliances();
}

function scUpdateLiveSummary() {
  const liveDevices = document.getElementById('live-devices');
  const liveKwh = document.getElementById('live-kwh');
  if (!liveDevices && !liveKwh) return;

  let units = 0;
  let totalWh = 0;
  scState.appliances.forEach(app => {
    if (app.selected) {
      units += app.qty;
      totalWh += app.power * app.hours * app.qty;
    }
  });

  if (liveDevices) liveDevices.innerText = String(units);
  if (liveKwh) liveKwh.innerText = (totalWh / 1000).toFixed(2);
}

// Bound products are injected from the section as FOUR groups (batteries /
// inverters / combos / allInOne); v20 merges the former 12V + 48V battery
// arrays into one "batteries" pool. Each product may carry capacityKwh /
// powerW / voltage metafields; scParseSpec() fills the gaps by parsing the
// title + tags.
// v10: products may also carry a `variants` array (injected from Liquid).
// Each variant is parsed the same way (variant metafields first, then the
// variant title + option values, then the product-level values), so the
// recommendation engine can pick the best-matching variant per product.
function scParseSpec(src, obj) {
  let cap = Number(obj.capacityKwh) || 0;
  let watts = Number(obj.powerW) || 0;
  let voltage = obj.voltage || '';

  if (!cap) {
    const kwh = src.match(/(\d+(?:\.\d+)?)\s*kwh/i);
    if (kwh) {
      cap = parseFloat(kwh[1]);
    } else {
      const ah = src.match(/(\d+(?:\.\d+)?)\s*ah/i);
      const v = src.match(/(\d+)\s*v\b/i);
      if (ah) {
        const volt = v ? parseInt(v[1], 10) : (src.indexOf('48') !== -1 ? 48 : 12);
        cap = (parseFloat(ah[1]) * volt) / 1000;
      }
    }
  }
  if (!watts) {
    // v10: (?!h) so "5.12kWh" never parses as 5120 W - only W / kW count.
    const w = src.match(/(\d+(?:\.\d+)?)\s*(k?)\s*w(?!h)\b/i);
    if (w) watts = parseFloat(w[1]) * (w[2] ? 1000 : 1);
  }
  if (!voltage) {
    const v = src.match(/(\d+)\s*v\b/i);
    if (v) voltage = v[1] + 'V';
  }
  return { capacityKwh: cap, powerW: watts, voltage: voltage };
}

function scParseProduct(p) {
  const src = (p.title || '') + ' ' + (p.tags || []).join(' ');
  const parsed = scParseSpec(src, p);
  const variants = Array.isArray(p.variants)
    ? p.variants.map(v => {
        const vsrc = (p.title || '') + ' ' + (v.title || '') + ' ' + [v.o1, v.o2, v.o3].filter(Boolean).join(' ');
        const vp = scParseSpec(vsrc, v);
        return Object.assign({}, v, {
          capacityKwh: vp.capacityKwh || parsed.capacityKwh,
          powerW: vp.powerW || parsed.powerW,
          voltage: vp.voltage || parsed.voltage
        });
      })
    : [];
  return Object.assign({}, p, parsed, { variants: variants });
}

function scGetBoundProducts() {
  const el = document.getElementById('sc-product-data');
  const empty = { batteries: [], inverters: [], combos: [], allInOne: [], camping: [] };
  if (!el) return empty;

  // v23.1 fix: Liquid's `for ... remove_last: ','` filter occasionally
  // leaves a stray trailing comma inside a variant array (e.g. a product
  // whose last variant has no metafields), producing JSON that fails
  // the strict JSON.parse(). We pre-strip `,\s*` immediately before
  // closing brackets / braces, which is what browsers already do.
  // This is a no-op when the JSON is well-formed.
  let text = el.textContent || '';
  text = text.replace(/,(\s*[}\]])/g, '$1');

  try {
    const data = JSON.parse(text);
    return {
      // v20: single battery pool (12V / 24V / 48V) - filtered by voltage later
      batteries: Array.isArray(data.batteries) ? data.batteries.map(scParseProduct) : [],
      inverters: Array.isArray(data.inverters) ? data.inverters.map(scParseProduct) : [],
      combos: Array.isArray(data.combos) ? data.combos.map(scParseProduct) : [],
      // v12: all-in-one power stations (portable battery+inverter combo units)
      allInOne: Array.isArray(data.allInOne) ? data.allInOne.map(scParseProduct) : [],
      // Camping-dedicated products - bypass scene filtering and classify into
      // Battery / Inverter / Portable Power at recommendation time.
      camping: Array.isArray(data.camping) ? data.camping.map(scParseProduct) : []
    };
  } catch (e) {
    return empty;
  }
}

// v22: tiny diagnostic row that shows how many products each bound
// collection injected - rendered above the recommendation cards so the
// merchant can immediately see if a pool is empty (no products / not
// published / wrong sales channel) without having to inspect HTML.
// v23.2: removed - merchants have confirmed bindings look correct (Battery 4 /
// Inverter 9 / Combo 14 / AIO 2), keep the function as a no-op so any leftover
// callers are harmless. To re-enable, restore the body and add the
// <div id="sc-debug-counts"> element back to the liquid template.
function scUpdateDebugCounts(bound) {
  return;
}

// v6: rank a product list by how closely (per-unit) capacity covers targetKwh.
// comboMode adds a small penalty for kits that need more than one unit, so a
// single larger kit usually wins over stacking many small ones.
// v10: when a product ships multiple variants with their own capacities, each
// variant is scored and the best one wins - the returned item carries that
// variant's id (variantId) so "Add to Cart" adds exactly the right variant.
function scVariantOptions(p) {
  const vs = (p.variants || []).filter(v => Number(v.capacityKwh) > 0);
  if (vs.length) {
    return vs.map(v => ({ capacityKwh: v.capacityKwh, powerW: v.powerW || 0, variantId: v.id, variantTitle: v.title || '' }));
  }
  return [{ capacityKwh: p.capacityKwh, powerW: p.powerW || 0, variantId: p.variantId, variantTitle: '' }];
}

// Camping stays as one flat product series. Titles and specifications are
// used only to rank products; no Battery / Inverter / Portable Power grouping
// is shown to the customer.
function scRankCampingProducts(products, targetKwh, peakWatts) {
  return (products || [])
    .filter(p => p && p.id)
    .map(p => {
      let best = null;
      scVariantOptions(p).forEach(opt => {
        const capacity = opt.capacityKwh > 0 ? opt.capacityKwh : 1;
        const coverage = capacity;
        const capacityScore = coverage >= targetKwh
          ? coverage / Math.max(targetKwh, 0.1)
          : 1 + (targetKwh - coverage) / Math.max(targetKwh, 0.1) * 2.2;
        const output = opt.powerW > 0 ? opt.powerW : 0;
        const powerScore = output > 0 && peakWatts > 0
          ? (output >= peakWatts ? output / peakWatts : 1 + (peakWatts - output) / peakWatts * 1.5)
          : 1;
        const score = capacityScore * 0.7 + powerScore * 0.3;
        if (!best || score < best.score) {
          best = {
            score,
            capacityKwh: capacity,
            coverage,
            ratio: coverage / Math.max(targetKwh, 0.1),
            powerW: output,
            variantId: opt.variantId,
            variantTitle: opt.variantTitle
          };
        }
      });
      return Object.assign({}, p, best || {}, { recommendedQty: 1, isCamping: true });
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, 4);
}

function scRankByCoverage(products, targetKwh, maxShow, comboMode, defaultKwh) {
  return products
    .filter(p => p && p.id)
    .map(p => {
      let best = null;
      scVariantOptions(p).forEach(opt => {
        const cap = opt.capacityKwh > 0 ? opt.capacityKwh : defaultKwh;
        // Battery Storage may need multiple units; kits and all-in-one
        // products are always recommended as one complete product.
        const qty = comboMode ? 1 : Math.max(1, Math.ceil(targetKwh / cap));
        const coverage = cap * qty;
        const ratio = coverage / targetKwh;
        let score = ratio >= 1 ? ratio : 1 + (1 - ratio) * 2.5;
        if (comboMode) score *= 1 + (qty - 1) * 0.12;
        if (!best || score < best.score) {
          best = { capacityKwh: cap, recommendedQty: qty, coverage: coverage, ratio: ratio, score: score, variantId: opt.variantId, variantTitle: opt.variantTitle };
        }
      });
      return Object.assign({}, p, {
        capacityKwh: best.capacityKwh,
        recommendedQty: best.recommendedQty,
        coverage: best.coverage,
        ratio: best.ratio,
        score: best.score,
        isCombo: !!comboMode,
        variantId: best.variantId != null ? best.variantId : p.variantId,
        variantTitle: best.variantTitle || ''
      });
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, maxShow);
}

// v6: rank hybrid inverters by how well their rated power covers the
// calculated peak load (sum of all selected appliance wattage).
// v10: variant-aware - a product with multiple power-rated variants is
// scored per variant and the best one is recommended.
function scInverterOptions(p) {
  const vs = (p.variants || []).filter(v => Number(v.powerW) > 0);
  if (vs.length) {
    return vs.map(v => ({ powerW: v.powerW, voltage: v.voltage || '', variantId: v.id, variantTitle: v.title || '' }));
  }
  return [{ powerW: p.powerW, voltage: p.voltage || '', variantId: p.variantId, variantTitle: '' }];
}

function scRankInverters(products, peakWatts, maxShow, targetVoltage) {
  return products
    .filter(p => p && p.id)
    .map(p => {
      let best = null;
      scInverterOptions(p).filter(opt => scVoltageMatches(opt.voltage, targetVoltage)).forEach(opt => {
        const watts = opt.powerW > 0 ? opt.powerW : 3000;
        const score = watts >= peakWatts ? watts / peakWatts : 1 + (peakWatts - watts) / peakWatts * 2;
        if (!best || score < best.score) {
          best = { powerW: watts, score: score, variantId: opt.variantId, variantTitle: opt.variantTitle };
        }
      });
      if (!best) return null;
      return Object.assign({}, p, {
        powerW: best.powerW,
        peakWatts: peakWatts,
        score: best.score,
        isInverter: true,
        variantId: best.variantId != null ? best.variantId : p.variantId,
        variantTitle: best.variantTitle || ''
      });
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .slice(0, maxShow);
}

// v10: capitalize the first letter of every English word ("every word's
// first letter capitalized"). Tokens that already contain an uppercase
// letter (kWh, 12V, DoD, W) are left untouched, and HTML entities like
// &middot; / &mdash; / &times; are preserved.
function scTitleCase(str) {
  return String(str).replace(/(^|[\s\/·\-])([a-z])(?![A-Za-z]*[A-Z])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

function scCalculateResults() {
  let totalWh = 0;
  let peakWatts = 0;
  scState.appliances.forEach(app => {
    if (app.selected) {
      totalWh += app.power * app.hours * app.qty;
      peakWatts += app.power * app.qty;
    }
  });

  const dailyKwh = totalWh / 1000;
  // 2 days autonomy, 80% DoD
  const batteryKwh = (dailyKwh * 2) / 0.8;
  // 5 peak sun hours, 80% system efficiency
  const solarWatts = (totalWh / 0.8) / 5;

  // v20: pick the voltage tier from the calculated battery need and keep
  // only batteries whose voltage matches that tier (unknown voltage stays).
  const voltage = scPickVoltage(batteryKwh);

  const boundRaw = scGetBoundProducts();
  scUpdateDebugCounts(boundRaw);
  // v24: scenario-aware filtering - products tagged scene:<key> are
  // prioritised for the chosen scenario; untagged products stay universal.
  // v25: scenario hard rules then wipe out whole categories that are
  // inappropriate for the chosen scenario (e.g. camping never recommends
  // whole-home All-in-One stations).
  // When Camping has a dedicated collection, that collection is ranked as
  // one flat product series. Otherwise the normal scene:camping path applies.
  const usingCampingDedicated = scState.scenario === 'camping' && boundRaw.camping && boundRaw.camping.length > 0;
  let bound;
  if (usingCampingDedicated) {
    bound = {
      batteries: [],
      inverters: [],
      combos:    [],
      allInOne:  []
    };
  } else {
    bound = {
      batteries: scApplyScenarioHardRules('batteries', scFilterByScenario(boundRaw.batteries, scState.scenario), scState.scenario),
      inverters: scApplyScenarioHardRules('inverters', scFilterByScenario(boundRaw.inverters, scState.scenario), scState.scenario),
      combos:    scApplyScenarioHardRules('combos',    scFilterByScenario(boundRaw.combos,    scState.scenario), scState.scenario),
      allInOne:  scApplyScenarioHardRules('allInOne',  scFilterByScenario(boundRaw.allInOne,  scState.scenario), scState.scenario)
    };
  }
  const spec = scBatterySpecs[voltage];
  const batteryList = bound.batteries.filter(p => scVoltageMatches(p.voltage, voltage));

  // ---- v6: system tier (drives the recommendation rationale) ----
  let tier;
  if (batteryKwh <= 1.2) {
    tier = {
      id: 'portable',
      label: 'Portable & Compact',
      note: scTitleCase(`Only ${dailyKwh.toFixed(1)} kWh/day &mdash; a portable kit or a single 12V battery usually covers this comfortably.`)
    };
  } else if (voltage === '12V') {
    tier = {
      id: '12v',
      label: '12V System',
      note: scTitleCase(`${dailyKwh.toFixed(1)} kWh/day &mdash; a 12V battery bank is the sweet spot for this load.`)
    };
  } else {
    tier = {
      id: '48v',
      label: '48V High-Capacity System',
      note: scTitleCase(`${dailyKwh.toFixed(1)} kWh/day &mdash; 48V keeps cables small and handles larger loads efficiently.`)
    };
  }

  // ---- v6: dynamic ranking ----
  // v9: only the top match per group is needed (one battery / one inverter /
  // one kit), ranked by how well each covers the calculated need.
  // v12: + all-in-one column (portable power stations).
  const combos = scRankByCoverage(bound.combos, batteryKwh, 1, true, spec.kwh);
  const batteries = scRankByCoverage(batteryList, batteryKwh, 1, false, spec.kwh);
  // Hybrid Inverter recommendations use the same calculated voltage tier as
  // Battery Storage (for example, 48V batteries require a 48V inverter).
  const inverters = scRankInverters(bound.inverters, Math.max(peakWatts, 600), 1, voltage);
  // AIOs are like combos: cover the calculated kWh with a single unit (or
  // stacked units), but they're typically smaller / portable so we treat
  // them as combos for scoring (small penalty for multi-unit) - they shine
  // for small/medium loads.
  const allInOne = scRankByCoverage(bound.allInOne, batteryKwh, 1, true, 1.2);

  // Fallback placeholders when the merchant has not bound products yet.
  if (!batteries.length) {
    const qty = Math.max(1, Math.ceil(batteryKwh / spec.kwh));
    batteries.push({
      id: null,
      title: `${voltage} Battery System`,
      desc: `Estimated need: ~${batteryKwh.toFixed(2)} kWh (${qty} x ${spec.voltage}V ${spec.ah}Ah). Bind a battery collection in the section settings.`,
      img: '',
      url: '#',
      variantId: null,
      voltage: voltage,
      capacityKwh: spec.kwh,
      recommendedQty: qty,
      coverage: qty * spec.kwh,
      ratio: (qty * spec.kwh) / batteryKwh,
      isCombo: false
    });
  }
  if (!inverters.length && bound.inverters.length === 0) {
    const watts = Math.max(1000, Math.ceil(peakWatts / 500) * 500);
    inverters.push({
      id: null,
      title: 'Hybrid Inverter',
      desc: `Recommended to convert and manage your ${voltage} system. Bind an inverter collection in the section settings.`,
      img: '',
      url: '#',
      variantId: null,
      powerW: watts,
      peakWatts: Math.max(peakWatts, 600),
      isInverter: true
    });
  }
  if (!allInOne.length) {
    // v26: skip the placeholder when the scenario explicitly excludes
    // All-in-One (e.g. camping). The 4th card column disappears
    // entirely so the recommendation grid reads as "AIO is not the right
    // category for this use case" rather than "you forgot to bind".
    const aioExcludedByScenario =
      scState.scenario &&
      scScenarioHardExclusions.allInOne &&
      scScenarioHardExclusions.allInOne.has(scState.scenario);
    if (!aioExcludedByScenario) {
      // v12: a placeholder only when the AIO group is explicitly empty -
      // it keeps the layout consistent (4 cards) while the merchant binds
      // the all-in-one collection.
      allInOne.push({
        id: null,
        title: 'All-In-One Power Station',
        desc: `An all-in-one unit (battery + inverter + MPPT in one box) sized for your ${batteryKwh.toFixed(1)} kWh daily need. Bind an all-in-one collection in the section settings.`,
        img: '',
        url: '#',
        variantId: null,
        capacityKwh: 1.0,
        recommendedQty: 1,
        coverage: 1.0,
        ratio: (Math.max(1, Math.ceil(batteryKwh / 1.0)) * 1.0) / batteryKwh,
        isCombo: true
      });
    }
  }

  document.getElementById('res-energy').innerText = dailyKwh.toFixed(2) + ' kWh';
  document.getElementById('res-wh').innerText = Math.round(totalWh) + ' Wh/day';
  document.getElementById('res-battery').innerText = batteryKwh.toFixed(2) + ' kWh';
  document.getElementById('res-solar').innerText = Math.round(solarWatts) + ' W';
  document.getElementById('res-voltage').innerText = voltage;

  if (usingCampingDedicated) {
    scRenderCampingProducts(scRankCampingProducts(boundRaw.camping, batteryKwh, peakWatts), tier);
    return;
  }
  scRenderProducts({ combos: combos, battery: batteries, inverter: inverters, allInOne: allInOne, tier: tier });
}

function scProductCardHtml(p, isTop) {
  const qty = p.recommendedQty || 1;
  const targetKwh = p.coverage ? (p.coverage / p.ratio) : 0;

  let badges = '';
  if (qty > 1) badges += `<span class="qty-badge">Recommended Qty: ${qty}</span>`;
  if (isTop && p.isCombo && qty === 1) badges += `<span class="best-badge">&#9733; Best Match</span>`;
  // v10: show which variant was selected for this recommendation
  if (p.variantTitle && String(p.variantTitle).trim().toLowerCase() !== 'default title') {
    badges += `<span class="variant-badge">Variant: ${p.variantTitle}</span>`;
  }

  let note = '';
  if (p.isInverter && p.peakWatts) {
    note = `<span class="match-note">${scTitleCase(`Rated ${p.powerW} W &middot; covers your ${p.peakWatts} W peak load`)}</span>`;
  } else if (p.coverage) {
    note = p.isCombo && qty === 1
      ? `<span class="match-note">${scTitleCase(`Covers ${p.coverage.toFixed(1)} kWh &middot; fits your ${targetKwh.toFixed(1)} kWh need`)}</span>`
      : `<span class="match-note">${scTitleCase(`${qty} &times; ${p.capacityKwh} kWh = ${p.coverage.toFixed(1)} kWh &middot; for your ${targetKwh.toFixed(1)} kWh need`)}</span>`;
  }

  // v23.2 fix: Liquid injects the image URL under the key `image`
  // (line ~161 of sections/solar-calculator.liquid), not `img`. Read
  // `image` and fall back to `img` so older sections that still emit
  // both keys keep working.
  const pImg = p.image || p.img || '';
  const imgHtml = pImg
    ? `<div class="img-wrap"><img src="${pImg}" alt="${p.title}" loading="lazy"></div>`
    : `<div class="img-wrap img-placeholder"><span>${p.isCombo ? 'Combo Kit' : (p.isInverter ? 'Inverter' : (p.voltage || 'Battery'))}</span></div>`;

  const descText = p.description || p.desc || '';
  const descHtml = descText ? `<p>${descText}</p>` : '';
  const reviewHtml = p.id
    ? `<div class="jdgm-widget jdgm-preview-badge sc-product-reviews" data-id="${p.id}" data-product-title="${String(p.title || '').replace(/"/g, '&quot;')}" data-product-handle="${String(p.handle || '').replace(/"/g, '&quot;')}">${p.reviewBadge || ''}</div>`
    : '';

  // v10: the Complete Kit card uses the exact same button labels as the
  // battery and inverter cards ("Add to Cart" / "Buy Now").
  const addLabel = qty > 1 ? `Add ${qty} to Cart` : 'Add to Cart';
  const buyLabel = 'Buy Now';
  // v21: unbound placeholder cards (id null, url '#') render NO button at
  // all - a dead "View Product" link confused shoppers.
  const buttons = p.variantId
    ? `<button class="btn-primary" onclick="scAddToCart(${p.variantId}, ${qty}, this)">${addLabel}</button>
       <button class="btn-buy" onclick="scBuyNow(${p.variantId}, ${qty}, this)">${buyLabel}</button>
       <a class="btn-details" href="${p.url}" target="_blank" rel="noopener noreferrer">View Details</a>`
    : (p.url && p.url !== '#'
      ? `<a class="btn-details" href="${p.url}" target="_blank" rel="noopener noreferrer">View Product</a>`
      : '');

  return `
    <div class="product-card ${p.isCombo ? 'combo' : ''}">
      ${imgHtml}
      <div class="info">
        <h3>${p.title}</h3>
        ${reviewHtml}
        <div class="badges">${badges}</div>
        ${descHtml}
        ${note}
        <div class="actions">${buttons}</div>
      </div>
    </div>`;
}

// v9: renders the recommendations as ONE horizontal row of columns -
//   Battery Storage | Hybrid Inverter | Complete Kit (+ All-in-One in v12)
// Each column shows a small category header and the single best product card
// for that category (the top item of each ranked group from
// scCalculateResults). Order follows the user request: battery first,
// inverter second, kit third, all-in-one fourth.
function scRenderCampingProducts(products, tier) {
  const container = document.getElementById('rec-list');
  const tierEl = document.getElementById('rec-tier');
  if (tierEl && tier) {
    tierEl.innerHTML = `<span class="sc-tier-chip">${tier.label}</span><span class="sc-tier-note">${tier.note}</span>`;
  }
  container.innerHTML = products.length ? `
    <div class="sc-camping-recs">
      ${products.map((product, index) => `
        <div class="sc-camping-rec">
          ${scProductCardHtml(product, index === 0)}
        </div>`).join('')}
    </div>` : '';
  scRefreshJudgeMe();
}

function scRenderProducts(groups) {
  const container = document.getElementById('rec-list');

  const tierEl = document.getElementById('rec-tier');
  if (tierEl && groups.tier) {
    tierEl.innerHTML = `<span class="sc-tier-chip">${groups.tier.label}</span><span class="sc-tier-note">${groups.tier.note}</span>`;
  }

  const top = (list) => (list && list.length ? list[0] : null);
  const combo = top(groups.combos);
  const battery = top(groups.battery);
  const inverter = top(groups.inverter);
  const aio = top(groups.allInOne);

  // helper: 4 icons (battery / inverter / kit / aio) tinted differently
  const col = (label, icon, p, accent) => {
    if (!p) return '';
    return `
      <div class="rec-col${accent ? ' rec-col-' + accent : ''}">
        <div class="rec-col-head">
          <span class="rec-col-ico" aria-hidden="true">${icon}</span>
          <span class="rec-col-label">${label}</span>
        </div>
        ${scProductCardHtml(p, accent === 'combo' || accent === 'aio')}
      </div>`;
  };

  container.innerHTML = `
    <div class="rec-row">
      ${col('Battery Storage', '&#128267;', battery, 'battery')}
      ${col('Hybrid Inverter', '&#9889;', inverter, 'inverter')}
      ${col('Complete Kit', '&#128230;', combo, 'combo')}
      ${col('All-in-One', '&#128268;', aio, 'aio')}
    </div>`;
  scRefreshJudgeMe();
}

function scRefreshJudgeMe() {
  if (window.jdgm && typeof window.jdgm.renderWidgets === 'function') {
    window.jdgm.renderWidgets();
  }
}

function scAddToCart(variantId, quantity, btn) {
  const originalText = btn.innerText;
  btn.disabled = true;
  btn.innerText = 'Adding...';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
  })
  .then(res => {
    if (!res.ok) throw new Error('add failed');
    return res.json();
  })
  .then(() => {
    btn.innerText = 'Added!';
    setTimeout(() => {
      btn.innerText = originalText;
      btn.disabled = false;
    }, 2000);
  })
  .catch(() => {
    btn.innerText = 'Failed - Retry';
    btn.disabled = false;
    setTimeout(() => { btn.innerText = originalText; }, 2000);
  });
}

function scBuyNow(variantId, quantity, btn) {
  const originalText = btn.innerText;
  btn.disabled = true;
  btn.innerText = 'Processing...';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
  })
  .then(res => {
    if (!res.ok) throw new Error('add failed');
    return res.json();
  })
  .then(() => {
    window.location.href = '/checkout';
  })
  .catch(() => {
    btn.innerText = 'Failed - Retry';
    btn.disabled = false;
    setTimeout(() => { btn.innerText = originalText; }, 2000);
  });
}

function scNextStep() {
  if (scCurrentStep === 1 && !scState.scenario) {
    alert('Please select a scenario.');
    return;
  }
  if (scCurrentStep === 2 && !scState.subtype) {
    alert('Please select a setup type.');
    return;
  }
  if (scCurrentStep === 3) {
    scCalculateResults();
  }

  if (scCurrentStep < 4) {
    scCurrentStep++;
    scUpdateView();
  }
}

function scPrevStep() {
  if (scCurrentStep > 1) {
    scCurrentStep--;
    scUpdateView();
  }
}

function scResetCalculator() {
  scCurrentStep = 1;
  scState.scenario = null;
  scState.subtype = null;
  scState.customSubtypeName = '';
  scState.customScenarioName = '';
  scCustomSeq = 0;
  // v8: visitor-added devices belong to the session - wipe them on reset.
  scState.appliances = scState.appliances.filter(a => !a.custom);
  // v11: nothing is pre-selected after a reset either - every device starts
  // unticked and the visitor chooses what they actually use.
  scState.appliances.forEach(app => {
    app.selected = false;
    app.qty = 1;
    app.hours = app.id === 'refrigerator' ? 6 : (app.id === 'lights' ? 4 : (app.id === 'router' ? 24 : 1));
  });
  document.querySelectorAll('.hdt-solar-calc .scenario-card').forEach(el => el.classList.remove('selected'));
  ['sc-custom-name', 'sc-custom-power', 'sc-custom-hours'].forEach(id => {
    const field = document.getElementById(id);
    if (field) field.value = '';
  });
  const customScenarioInput = document.querySelector('.sc-custom-scenario-input');
  if (customScenarioInput) customScenarioInput.value = '';
  const recList = document.getElementById('rec-list');
  if (recList) recList.innerHTML = '';
  const recTier = document.getElementById('rec-tier');
  if (recTier) recTier.innerHTML = '';
  scUpdateView();
}

function scUpdateView() {
  for (let i = 1; i <= 4; i++) {
    document.getElementById(`step-${i}`).classList.remove('active');
    const ind = document.getElementById(`ind-${i}`);
    ind.classList.remove('active', 'completed');
    if (i < scCurrentStep) ind.classList.add('completed');
    if (i === scCurrentStep) ind.classList.add('active');
  }

  document.getElementById(`step-${scCurrentStep}`).classList.add('active');

  if (scCurrentStep === 2) {
    scRenderSubtypes();
  } else if (scCurrentStep === 3) {
    scRenderAppliances();
  }

  const btnBack = document.getElementById('btn-back');
  const btnNext = document.getElementById('btn-next');

  // Back button: always visible, disabled on the first step
  btnBack.disabled = scCurrentStep === 1;
  btnBack.style.display = 'inline-flex';

  if (scCurrentStep === 4) {
    btnNext.style.display = 'none';
  } else {
    btnNext.style.display = 'inline-flex';
    // v5: manual navigation - Next stays disabled until a choice is made.
    if (scCurrentStep === 1) {
      btnNext.disabled = !scState.scenario;
    } else if (scCurrentStep === 2) {
      btnNext.disabled = !scState.subtype;
    } else {
      btnNext.disabled = false;
    }
    const nextLabel = btnNext.querySelector('.btn-label');
    if (nextLabel) nextLabel.innerText = scCurrentStep === 3 ? 'Calculate' : 'Next';
  }

  const btnRestart = document.getElementById('btn-restart');
  if (btnRestart) {
    btnRestart.style.display = scCurrentStep === 4 ? 'inline-flex' : 'none';
  }
}

// v5: initialize the wizard (Next disabled until a scenario is chosen).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scUpdateView);
} else {
  scUpdateView();
}
